import { memoize } from "lodash";
import moment from "moment";
import { createSelector } from "reselect";
import { getTimeWorkdays } from "~/store/selectors/selector.configOptions";
import { getWeeklyUserCapacityArrayById } from "~/store/selectors/selector.weeklyWorkerCapacity";
import { getIsDateWorkday } from "~/utils/workday";

export const getAllDaysOff = (state) => state.daysOff;

export const getDaysOffArray = createSelector(getAllDaysOff, (daysOff) => Object.values(daysOff));

export const getGanttDaysOff = createSelector(getDaysOffArray, (daysOff) =>
  daysOff.map((dayOff) => ({
    start: dayOff.start_date,
    end: dayOff.end_date,
    repeat_yearly: dayOff.repeat_yearly,
  }))
);

export const getDaysOffList = createSelector(getDaysOffArray, (daysOff) =>
  daysOff.map((dayOff) => ({
    id: dayOff.id,
    name: dayOff.name,
    startDate: dayOff.start_date,
    endDate: dayOff.end_date,
    repeatYearly: dayOff.repeat_yearly || false,
  }))
);

export const makeDaysOffForSelectDateSelector = (type = "global_day_off") =>
  createSelector(getDaysOffArray, (daysOff) =>
    daysOff.map((dayOff) => {
      return {
        id: dayOff.id,
        title: dayOff.name,
        from: dayOff.start_date,
        to: dayOff.end_date,
        repeating: dayOff.repeat_yearly || false,
        type,
      };
    })
  );

export const makeGetUserNonWorkingDaysSelector = (userId) => {
  return createSelector(getWeeklyUserCapacityArrayById(userId), getTimeWorkdays, (userWeeklyCapacity, timeWorkdays) => {
    return timeWorkdays.filter((index) => userWeeklyCapacity[+index] === 0);
  });
};

const nonWorkingDaysByDateRaw = (daysOff, _date) => {
  const date = typeof _date === "number" ? moment.unix(_date) : moment(_date);
  const result = daysOff.filter((dayOff) => {
    let start = moment.unix(dayOff.start_date).utc();
    let end = moment.unix(dayOff.end_date).utc();

    if (dayOff.repeat_yearly) {
      if (start.year() < end.year()) {
        if (start.month() === date.month()) {
          start = start.set("year", date.year());
          end = end.set("year", date.year() + 1);
        } else {
          start = start.set("year", date.year() - 1);
          end = end.set("year", date.year());
        }
      } else {
        start = start.set("year", date.year());
        end = end.set("year", date.year());
      }
    }

    return moment.utc(date.format("YYYY-MM-DD")).isBetween(start, end, "day", "[]");
  });
  return result;
};

const resolver = (arg1, arg2) => {
  const max = arg1.reduce((prev, next) => {
    prev += next.updated_on;
    return prev;
  }, 0);

  return `${max}-${arg2.toString()}`;
};

export const nonWorkingDaysByDate = memoize(nonWorkingDaysByDateRaw, resolver);

export const getNonWorkingDayByDate = createSelector(
  getDaysOffArray,
  (state, date) => date,
  (daysOff, date) => {
    return nonWorkingDaysByDate(daysOff, date);
  }
);

export const getIsWorkday = createSelector(
  getTimeWorkdays,
  getGanttDaysOff,
  (state, date) => {
    if (typeof date === "number") {
      return moment.unix(date);
    }
    return moment.utc(date);
  },
  (workdays, daysOff, date) => {
    return getIsDateWorkday(workdays, daysOff, date);
  }
);

export const getIsWorkdaysRange = createSelector(
  getTimeWorkdays,
  getGanttDaysOff,
  (state, from) => {
    if (typeof from === "number") {
      return moment.unix(from);
    }
    return moment.utc(from);
  },
  (state, from, to) => {
    if (typeof to === "number") {
      return moment.unix(to);
    }
    return moment.utc(to);
  },
  (workdays, daysOff, from, to) => {
    if (to.isBefore(from)) {
      return null;
    }
    let result = {};
    let currentDate = from.clone();
    while (currentDate.isSameOrBefore(to)) {
      result[currentDate.utc().format("YYYY-MM-DD")] = getIsDateWorkday(workdays, daysOff, currentDate);
      currentDate.add(1, "day");
    }

    return result;
  }
);
